import React, { useState, useMemo } from 'react';
import { Spinner } from 'react-bootstrap';
import { ChevronRight, ChevronDown, History } from 'lucide-react';
import { useUsersData } from '../../Helpers/UsersContext';
import {
    useArchiveHistory,
    DEFAULT_HISTORY_LIMIT,
} from '../../hooks/archive/useArchiveHistory';
import {
    HistoryGroupBlock,
    createUserMap,
} from '../../Components/History/HistoryComponents';
import '../Level/LevelHistorySection.css'; // Reuse the same styles

// ── Props ───────────────────────────────────────────────────────────

interface RoomHistorySectionProps {
    buildingKey: string;
    roomKey: string;
}

// ── Component ───────────────────────────────────────────────────────

const RoomHistorySection: React.FC<RoomHistorySectionProps> = ({
    buildingKey,
    roomKey,
}) => {
    const { users, loadingUsers } = useUsersData();
    const [isExpanded, setIsExpanded] = useState(false);

    // O(1) user lookup map
    const userMap = useMemo(() => createUserMap(users), [users]);

    const {
        loading,
        error,
        groupedHistory,
    } = useArchiveHistory(buildingKey, roomKey, 'rooms');

    const hasHistory = groupedHistory.length > 0;
    const totalChanges = groupedHistory.reduce(
        (sum, group) => sum + group.entries.reduce((s, e) => s + e.changes.length, 0),
        0
    );

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setIsExpanded(!isExpanded);
        }
    };

    return (
        <div className="level-history-section">
            <div
                className="level-history-header"
                role="button"
                tabIndex={0}
                onClick={() => setIsExpanded(!isExpanded)}
                onKeyDown={handleKeyDown}
            >
                <span className="level-history-chevron">
                    {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                </span>
                <History size={18} className="level-history-icon" />
                <span className="level-history-title">Historik</span>
                {!loading && hasHistory && (
                    <span className="level-history-badge">
                        {totalChanges} ändringar
                    </span>
                )}
            </div>

            {isExpanded && (
                <div className="level-history-body">
                    {loading && (
                        <div className="level-history-loading">
                            <Spinner animation="border" size="sm" />
                            <span>Laddar historik...</span>
                        </div>
                    )}

                    {error && (
                        <p className="text-danger">{error}</p>
                    )}

                    {!loading && !hasHistory && (
                        <p className="text-muted">Inga ändringar har registrerats</p>
                    )}

                    {!loading && hasHistory && (
                        <div className="level-history-group">
                            {groupedHistory.slice(0, DEFAULT_HISTORY_LIMIT).map(group => (
                                <HistoryGroupBlock
                                    key={group.groupKey}
                                    group={group}
                                    userMap={userMap}
                                    loadingUsers={loadingUsers}
                                    showEntityName={false}
                                />
                            ))}
                            {groupedHistory.length > DEFAULT_HISTORY_LIMIT && (
                                <p className="text-muted level-history-more">
                                    +{groupedHistory.length - DEFAULT_HISTORY_LIMIT} fler dagar
                                </p>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default RoomHistorySection;
